import { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import type { BoardroomBrand, BoardroomReportKey } from '../../../types';
import BrandReport from './BrandReport';
import useBoardRefresh from './useBoardRefresh';

/** How often to look again while searches are still landing. */
const POLL_MS = 15_000;

/**
 * One company's brand report, fetched by the card's subject (the company id),
 * for `ProfilePane`'s reason: the page holds one report per kind, and two
 * brand cards share that kind.
 *
 * While searches are still out it looks again every few seconds, so the
 * report fills in as they land.
 */
export default function BrandPane({
  boardroomId,
  subject,
  onOpen,
  roomName,
  refreshKey,
}: {
  boardroomId: number;
  subject: string;
  onOpen: (itemId: number) => void;
  roomName?: string;
  refreshKey?: string;
}) {
  const [data, setData] = useState<BoardroomBrand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [writing, setWriting] = useState(false);
  const [tick, setTick] = useState(0);
  // On the board nothing passes a refresh key: it listens itself, like the
  // other cards. Its own look-again only runs while searches are still out.
  const fresh = useBoardRefresh(boardroomId, refreshKey === undefined);

  const load = useCallback(() => {
    return api
      .getBoardroomReport<BoardroomBrand>(boardroomId, 'brand' as BoardroomReportKey, { subject })
      .then(d => {
        setData(d);
        setError(null);
      })
      .catch(() => setError('That company is not on this board any more.'));
  }, [boardroomId, subject]);

  useEffect(() => {
    void load();
  }, [load, refreshKey, tick, fresh]);

  // STILL LANDING: look again shortly.
  const landing = Boolean(data?.progress && data.progress.pending > 0);
  useEffect(() => {
    if (!landing) return;
    const t = window.setTimeout(() => setTick(x => x + 1), POLL_MS);
    return () => window.clearTimeout(t);
  }, [landing, tick]);

  const restart = async () => {
    setRestarting(true);
    try {
      await api.startBoardroomBrandSearches(boardroomId, Number(subject));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the searches');
    } finally {
      setRestarting(false);
    }
  };

  const setSide = async (side: 'ours' | 'theirs') => {
    // Shown at once; the server answers with the findings re-read for it.
    setData(d => (d ? { ...d, side } : d));
    try {
      setData(await api.setBoardroomBrandSide(boardroomId, Number(subject), side));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change sides');
    }
  };

  const [watchBusy, setWatchBusy] = useState(false);
  const watch = async () => {
    setWatchBusy(true);
    try {
      await api.watchBoardroomBrandSearches(boardroomId, Number(subject));
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the watch');
    } finally {
      setWatchBusy(false);
    }
  };

  const writeUp = async () => {
    setWriting(true);
    try {
      setData(await api.writeBoardroomBrandReport(boardroomId, Number(subject)));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not write it up');
    } finally {
      setWriting(false);
    }
  };

  if (error && !data) return <div className="text-xs text-gray-500 dark:text-gray-400">{error}</div>;
  if (!data) return <div className="text-xs text-gray-400 dark:text-gray-500">Reading the board…</div>;
  return (
    <>
      {error && <div className="mb-2 text-xs text-red-600 dark:text-red-400">{error}</div>}
      <BrandReport data={data} onOpen={onOpen} onRestart={restart} restarting={restarting} onSide={setSide} onWriteUp={writeUp} writing={writing} roomName={roomName} onWatch={watch} watchBusy={watchBusy} />
    </>
  );
}
